// vue
import { vm } from '@/main';
import { useRouter } from '@/router';
// import { useStore } from 'vuex';
import { useStore } from '@/store';
import { useNotify } from '@/vue3/hooks/notifyHook';
// axios
import axios, { AxiosError } from 'axios';
const router = useRouter();

// import { useToast } from 'vue-toastification';

export const useAxios = () => {
    // hooks
    const store = useStore();
    const { errorToast, warnToast } = useNotify();

    let doneArray: boolean[] = [];
    // methods
    const handleError = (err: AxiosError, i: number) => {
        doneArray[i] = true;

        if (err.response && err.response.status === 401) {
            errorToast('Permission denied.');

            throw err;
        }

        if (err.response && err.response.status === 504) {
            errorToast(
                'There is currently a problem with our servers. Please get in touch with Clere if this problem persists.',
            );

            throw err;
        }

        if (err.response && err.response.data.message) {
            errorToast(err.response.data.message);
        } else if (err.response && err.response.data.errors) {
            let errors = '';
            err.response.data.errors.forEach((error: string, i: number) => {
                errors += '    ' + error;
                if (i !== errors.length - 1) {
                    errors += '\n';
                }
            });

            errorToast('Validation error:\n' + errors);
        }
        throw err;
    };

    const setDelayWarning = (delay?: number) => {
        const i = doneArray.length;
        doneArray.push(false);

        if (delay) {
            setTimeout(() => {
                if (!doneArray[i]) {
                    warnToast(
                        'This operation is taking longer than expected. Please get in touch with Clere if this problem persists.',
                    );
                }
            }, delay);
        }

        return i;
    };

    // eslint-disable-next-line
    const get = (url: string, delay?: number, options?: any): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .get(url, {
                cancelToken: store.state.cancelToken.token,
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                },
                ...options,
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });

        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const post = (
        url: string,
        data: any,
        delay?: number,
        options?: any,
    ): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .post(url, data, {
                cancelToken: store.state.cancelToken.token,
                headers: {
                    Authorization: authToken
                        ? 'Bearer ' + JSON.parse(authToken).token
                        : null,
                },
                ...options,
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const postMultipart = (url: string, data: any, delay?: number): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .post(url, data, {
                cancelToken: store.state.cancelToken.token,
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                    'Content-Type': 'multipart/form-data',
                },
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const put = (url: string, data: any, delay?: number): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .put(url, data, {
                cancelToken: store.state.cancelToken.token,
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                },
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const deleteId = (url: string, delay?: number): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .delete(url, {
                cancelToken: store.state.cancelToken.token,
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                },
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const deleteObject = (url: string, data: any, delay?: number): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .delete(url, {
                cancelToken: store.state.cancelToken.token,
                data,
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                },
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    // eslint-disable-next-line
    const download = (url: string, delay?: number): any => {
        let authToken = store?.state?.authToken;

        if (JSON.parse(localStorage.getItem('tempAuthToken') as string)) {
            authToken = localStorage.getItem('tempAuthToken');
        }

        if (!authToken) {
            return null;
        }

        const i = setDelayWarning(delay);

        const axiosInstance = axios
            .get(url, {
                cancelToken: store.state.cancelToken.token,
                responseType: 'blob',
                headers: {
                    Authorization: 'Bearer ' + JSON.parse(authToken).token,
                },
            })
            .catch((err: AxiosError) => {
                handleError(err, i);
            });
        axiosInstance.then((response) => {
            doneArray[i] = true;
            if (
                response &&
                response.data &&
                response.data.is_maintenance_mode
            ) {
                store.commit('setIsMaintenanceMode', true);
                router.push({ path: '/app.vue' }); // Redirects to the root route
            }
        });
        return axiosInstance;
    };

    return {
        get,
        post,
        postMultipart,
        put,
        deleteObject,
        deleteId,
        download,
    };
};
